/**
 * `MergeMemberModal` — absorb one member profile into another, from the member
 * page's "+" menu. Dispatches the `merge_member` canonical action, which
 * re-parents bookings / payments / packages / memberships / flows / tags /
 * conversations / channel links in one transaction and soft-deletes the
 * absorbed row.
 *
 * The counterpart is found with the same debounced `lookupOptions("members",
 * q)` search `TransferCreditsModal` and the calendar use.
 *
 * WHY THIS EXISTS AS A DEDICATED CONTROL rather than a generic action form:
 *
 *   1. DIRECTION. `merge_member` takes two ids that look interchangeable and
 *      are not — one profile is destroyed. The operator opens this from a
 *      member page, so the sensible default is "this profile is absorbed", but
 *      they may equally be standing on the survivor, so the direction is
 *      swappable and stated in words rather than field names.
 *
 *   2. CONSEQUENCE. Merging is irreversible (`compensation:
 *      noop:single-step-write` — the transaction is the atomic unit; there is
 *      no undo). The counts of what is about to move are shown BEFORE the
 *      confirm, from data already loaded on the page.
 *
 *   3. TIMING. Merges are cheapest before money and schedule rows exist — the
 *      common case is a placeholder minted by a first Instagram DM or WhatsApp
 *      message being folded into a real account. This cannot enforce that
 *      ordering, but it makes a violation visible at the moment of decision
 *      instead of silent.
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import {
  buildPreviewCarryNotes,
  buildPreviewIdentityRows,
  buildPreviewMoveRows,
  describeMergeCounts,
  describeFlowsEnded,
  describeMergeResult,
  mergeDirection,
  previewWarningText,
  type MergeMemberCounts,
  type MergeMovedCounts,
  type MergePreview,
} from "../lib/merge-member";

export type { MergeMemberCounts } from "../lib/merge-member";

interface MemberOption {
  readonly value: string;
  readonly label: string;
  readonly sublabel?: string;
}

export function MergeMemberModal({
  memberId,
  memberName,
  counts,
  onDone,
}: {
  /** The member whose page this was opened from. */
  readonly memberId: string;
  readonly memberName: string;
  /** This member's money/schedule rows, for the pre-merge warning. */
  readonly counts: MergeMemberCounts;
  /** Called after a successful merge (refresh / navigate away). */
  readonly onDone: (survivorMemberId: string) => void;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ReadonlyArray<MemberOption> | null>(
    null,
  );
  const [searching, setSearching] = useState(false);
  const [otherId, setOtherId] = useState("");
  const [otherLabel, setOtherLabel] = useState("");
  /** false ⇒ THIS profile is absorbed (the default). */
  const [keepThis, setKeepThis] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /** Server-computed preview of THIS direction; null until it loads. */
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q === "" || otherId !== "") {
      setOptions(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const res = await api.lookupOptions("members", q);
          if (!cancelled) {
            // Never offer this member as their own counterpart.
            setOptions(res.options.filter((o) => o.value !== memberId));
          }
        } catch (e) {
          if (!cancelled) {
            setError(`Couldn't load members: ${(e as Error).message}`);
          }
        } finally {
          if (!cancelled) setSearching(false);
        }
      })();
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, otherId, memberId]);

  const absorbedLabel = keepThis ? otherLabel : memberName;
  const survivorLabel = keepThis ? memberName : otherLabel;

  // Load the preview whenever the pair OR the direction changes. Re-fetching on
  // a swap is not optional: the whole point is that the two directions have
  // DIFFERENT consequences, and a stale preview would describe the other one.
  // Confirmation is also cleared on every change, so nobody can tick the box
  // against one preview and then merge a different pair.
  useEffect(() => {
    if (otherId === "") {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const direction = mergeDirection({
      thisMemberId: memberId,
      otherMemberId: otherId,
      keepThis,
    });
    let cancelled = false;
    setPreviewing(true);
    setPreview(null);
    setPreviewError(null);
    void (async () => {
      try {
        const r = await dispatchAction("preview_member_merge", direction);
        if (cancelled) return;
        if (r.ok) {
          setPreview(r.response as MergePreview);
        } else {
          setPreviewError(r.message || r.code);
        }
      } catch (e) {
        if (!cancelled) setPreviewError((e as Error).message);
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [otherId, keepThis, memberId]);

  const identityRows = preview === null ? [] : buildPreviewIdentityRows(preview);
  const moveRows = preview === null ? [] : buildPreviewMoveRows(preview.moves);
  const losesSomething = identityRows.some((r) => r.effect === "lost");
  const carryNotes =
    preview === null ? [] : buildPreviewCarryNotes(preview, survivorLabel);
  const flowsEndingNote =
    preview === null
      ? ""
      : describeFlowsEnded({
          count: preview.flows_ending,
          tense: "preview",
          survivorLabel,
        });
  // Only warned about when THIS profile is the one being closed — when the
  // operator swaps, the rows counted here are the survivor's and stay put.
  const movingSummary = keepThis ? "" : describeMergeCounts(counts);

  const submit = useCallback(async () => {
    if (otherId === "") {
      setError("Pick the other profile first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const direction = mergeDirection({
        thisMemberId: memberId,
        otherMemberId: otherId,
        keepThis,
      });
      const r = await dispatchAction("merge_member", {
        ...direction,
        ...(reason.trim() !== "" ? { reason: reason.trim() } : {}),
      });
      setBusy(false);
      if (!r.ok) {
        setError(`Merge failed: ${r.message || r.code}`);
        return;
      }
      setDone(
        describeMergeResult(
          (r.response ?? {}) as MergeMovedCounts,
          survivorLabel,
        ),
      );
      onDone(direction.to_member_id);
    } catch (e) {
      setBusy(false);
      setError((e as Error).message);
    }
  }, [otherId, keepThis, memberId, reason, survivorLabel, onDone]);

  if (done !== null) {
    return <p className="xfer-done">✅ {done}</p>;
  }

  return (
    <div className="xfer-modal">
      <div className="xfer-field">
        <span>Other profile</span>
        {otherId !== "" ? (
          <div className="xfer-dest-chip">
            <span>{otherLabel}</span>
            <button
              type="button"
              className="xfer-dest-clear"
              onClick={() => {
                setOtherId("");
                setOtherLabel("");
                setQuery("");
                setConfirmed(false);
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder="Search by name, email or phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {searching ? <p className="xfer-hint">Searching…</p> : null}
            {options !== null && options.length > 0 ? (
              <ul className="xfer-dest-list">
                {options.map((o) => (
                  <li key={o.value}>
                    <button
                      type="button"
                      onClick={() => {
                        setOtherId(o.value);
                        setOtherLabel(o.label);
                        setOptions(null);
                      }}
                    >
                      {o.label}
                      {o.sublabel ? (
                        <span className="xfer-dest-sub"> · {o.sublabel}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {options !== null && options.length === 0 && !searching ? (
              <p className="xfer-hint">No members match.</p>
            ) : null}
          </>
        )}
      </div>

      {otherId !== "" ? (
        <>
          <div className="merge-direction">
            <p className="merge-direction-line">
              <strong>{absorbedLabel}</strong> closes. Everything on it moves to{" "}
              <strong>{survivorLabel}</strong>.
            </p>
            <button
              type="button"
              className="merge-swap"
              onClick={() => {
                setKeepThis((v) => !v);
                setConfirmed(false);
              }}
            >
              Swap — keep {absorbedLabel} instead
            </button>
          </div>

          {previewing ? (
            <p className="xfer-hint">Working out what would move…</p>
          ) : null}

          {previewError !== null ? (
            <p className="merge-warning">
              Couldn&apos;t load the preview: {previewError}. Merge anyway only
              if you are sure — you will not see what moves.
            </p>
          ) : null}

          {preview !== null ? (
            <div className="merge-preview">
              {/* The side-by-side. Closing profile left, surviving right, and
                  what the survivor is left holding. Every verdict is the
                  SERVER's — the same rules the merge itself applies. */}
              <table className="merge-preview-table table-cards">
                <thead>
                  <tr>
                    <th />
                    <th>
                      <span className="merge-col-role">Closing</span>
                      <span className="merge-col-name">{absorbedLabel}</span>
                    </th>
                    <th>
                      <span className="merge-col-role">Keeping</span>
                      <span className="merge-col-name">{survivorLabel}</span>
                    </th>
                    <th>
                      <span className="merge-col-role">After merge</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {identityRows.map((row) => (
                    <tr key={row.column} className={`merge-row-${row.effect}`}>
                      <th className="table-card-title" scope="row">{row.label}</th>
                      <td data-label="Closing" className={row.effect === "moves" ? "merge-from" : ""}>
                        {row.absorbedValue ?? <span className="merge-empty">—</span>}
                      </td>
                      <td data-label="Keeping">
                        {row.survivorValue ?? <span className="merge-empty">—</span>}
                      </td>
                      <td data-label="After merge" className="merge-result">
                        {row.resultValue ?? <span className="merge-empty">—</span>}
                        {row.effect === "moves" ? (
                          <span className="merge-badge merge-badge-move">
                            moves over
                          </span>
                        ) : null}
                        {row.effect === "lost" ? (
                          <span className="merge-badge merge-badge-lost">
                            lost{row.note !== null ? ` — ${row.note}` : ""}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {preview.metadata_keys_carried.length > 0 ? (
                <p className="merge-preview-note">
                  Also carried over: {preview.metadata_keys_carried.join(", ")}.
                </p>
              ) : null}

              {carryNotes.map((note) => (
                <p key={note.key} className="merge-preview-note">
                  {note.text}
                </p>
              ))}

              {moveRows.length > 0 ? (
                <p className="merge-preview-note">
                  Moving to {survivorLabel}:{" "}
                  {moveRows.map((m) => `${m.count} ${m.label}`).join(", ")}.
                </p>
              ) : (
                <p className="merge-preview-note">
                  Nothing else to move — {absorbedLabel} has no bookings,
                  payments or history attached.
                </p>
              )}
              {flowsEndingNote !== "" ? (
                <p className="merge-preview-note">{flowsEndingNote}</p>
              ) : null}

              {preview.warnings.map((code) => {
                const text = previewWarningText(
                  code,
                  absorbedLabel,
                  survivorLabel,
                );
                return text === null ? null : (
                  <p key={code} className="merge-warning">
                    {text}
                  </p>
                );
              })}
            </div>
          ) : null}

          {/* Fallback for the moment before the preview lands: the page's own
              counts, which is all this modal used to have. */}
          {preview === null && !previewing && movingSummary !== "" ? (
            <p className="merge-warning">
              This profile already has {movingSummary}. Merging moves them to{" "}
              {survivorLabel}; it is easiest to merge before any money or
              bookings exist.
            </p>
          ) : null}

          <label className="xfer-field">
            <span>Reason (optional)</span>
            <input
              type="text"
              maxLength={500}
              placeholder="e.g. duplicate created by their first Instagram DM"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>

          <label className="merge-confirm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span>
              I understand this cannot be undone — {absorbedLabel} will be
              closed.
              {losesSomething
                ? " I have seen which details are lost and want to merge anyway."
                : ""}
            </span>
          </label>
        </>
      ) : null}

      <div className="xfer-actions">
        <button
          type="button"
          className="xfer-submit"
          disabled={busy || otherId === "" || !confirmed}
          onClick={() => void submit()}
        >
          {busy ? "Merging…" : "Accept and merge"}
        </button>
      </div>
      {error !== null ? <p className="cal-action-error">{error}</p> : null}
    </div>
  );
}
